import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';

/**
 * The "More" menu on the client invoice header (D98.2).
 *
 * One accessible menu replaces the row of header buttons. It only lays items out: each item
 * arrives with the page's own handler and gating (`hidden` / `disabled` / `title`), so the menu
 * decides nothing about who may do what. An item with `to` is a plain link, not a button.
 */
export interface MoreMenuItem {
  key: string;
  label: string;
  onClick?: () => void;
  /** A plain link instead of a button (e.g. the new credit note). */
  to?: string;
  /** Link items only: runs on click before navigating (preventDefault to stop it) - the page's unsaved-changes guard. */
  onNavigate?: (e: React.MouseEvent, to: string) => void;
  disabled?: boolean;
  title?: string;
  /** Left out entirely - the page's permission / status gate said no. */
  hidden?: boolean;
}

const ITEM = 'block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white';

export const MoreMenu: React.FC<{ items: MoreMenuItem[] }> = ({ items }) => {
  // Where the open menu sits (fixed, under the button's right edge); null = closed.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const open = pos !== null;
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const r = rootRef.current?.getBoundingClientRect();
    setPos(r ? { top: r.bottom + 4, right: window.innerWidth - r.right } : null);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // The menu is portalled, so a press inside it is not inside rootRef.
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !menuRef.current?.contains(t)) setPos(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPos(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <div className="relative" ref={rootRef}>
      <Button aria-haspopup="menu" aria-expanded={open} onClick={() => (open ? setPos(null) : place())}>
        More
      </Button>
      {pos && createPortal(
        // D185.6: portalled to body like ModuleInfoButton. Inside the header (z-20) it painted under
        // the open line drawer (z-40) and the page's other sticky layers; z-50 sits over the drawer.
        <div
          ref={menuRef}
          role="menu"
          aria-label="More actions"
          className="fixed z-50 min-w-[220px] rounded-md border border-slate-200 bg-white py-1 shadow-lg"
          style={{ top: pos.top, right: pos.right }}
        >
          {items.filter((i) => !i.hidden).map((i) =>
            i.to ? (
              <Link key={i.key} role="menuitem" to={i.to} title={i.title} className={ITEM} onClick={(e) => {
                  setPos(null);
                  i.onNavigate?.(e, i.to!);
                }}>
                {i.label}
              </Link>
            ) : (
              <button
                key={i.key}
                type="button"
                role="menuitem"
                disabled={i.disabled}
                title={i.title}
                className={ITEM}
                onClick={() => {
                  setPos(null);
                  i.onClick?.();
                }}
              >
                {i.label}
              </button>
            )
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
